import React, { useState } from 'react';
import { 
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  CogIcon,
  ShieldCheckIcon,
  CloudIcon
} from '@heroicons/react/24/outline';

interface NavItem {
  id: string;
  title: string;
  icon?: React.ComponentType<any>;
  children?: NavItem[];
  content?: string;
}

const navigationData: NavItem[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: DocumentTextIcon,
    children: [
      { id: 'overview', title: 'Overview', content: 'getting-started-overview' },
      { id: 'quick-start', title: 'Quick Start Guide', content: 'quick-start' }
    ]
  },
  {
    id: 'job-management',
    title: 'Job Management',
    icon: CogIcon,
    children: [
      { id: 'job-overview', title: 'Job Overview', content: 'job-management' },
      { id: 'creating-jobs', title: 'Creating Jobs', content: 'quick-start' },
      { id: 'scheduling', title: 'Scheduling & Assignment', content: 'job-management' }
    ]
  },
  {
    id: 'customers-sites',
    title: 'Customers & Sites',
    icon: DocumentTextIcon,
    children: [
      { id: 'customer-management', title: 'Customer Management', content: 'customer-management' },
      { id: 'site-management', title: 'Site Management', content: 'customer-management' }
    ]
  },
  {
    id: 'certificates',
    title: 'Certificates',
    icon: ShieldCheckIcon,
    children: [
      { id: 'generating-certificates', title: 'Generating Certificates', content: 'quick-start' },
      { id: 'certificate-types', title: 'Certificate Types', content: 'job-management' }
    ]
  },
  {
    id: 'integrations',
    title: 'API & Integrations',
    icon: CloudIcon,
    children: [
      { id: 'api-reference', title: 'API Reference', content: 'api-reference' },
      { id: 'webhooks', title: 'Webhooks', content: 'webhooks' }
    ]
  }
];

const helpGuidesNavigation: NavItem[] = [
  {
    id: 'video-tutorials',
    title: 'Video Tutorials',
    icon: DocumentTextIcon,
    content: 'video-tutorials'
  },
  {
    id: 'interactive-demos',
    title: 'Interactive Demos',
    icon: CogIcon,
    content: 'interactive-demos'
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: ShieldCheckIcon,
    content: 'troubleshooting'
  }
];

const contentData: { [key: string]: any } = {
  'getting-started-overview': {
    title: 'Getting Started with Certs',
    description: 'Welcome to Certs - your comprehensive certificate management platform. This guide will help you get up and running quickly.',
    sections: [
      {
        id: 'what-is-certs',
        title: 'What is Certs?',
        content: `Certs is a modern certificate management platform designed for businesses that need to manage safety certificates, compliance documentation, and regulatory requirements. Built with efficiency and compliance in mind, Certs streamlines your entire certification workflow from job creation to certificate delivery.`
      },
      {
        id: 'key-features',
        title: 'Key Features',
        content: `**Job Management**
• Create and track certification jobs with multi-step workflows
• Assign engineers and schedule appointments
• Manage customer and site information

**Certificate Generation**
• Generate professional safety certificates
• Automated compliance reporting
• Digital certificate delivery

**Customer & Site Management**
• Comprehensive customer database
• Multiple site management per customer
• Contact and location tracking

**Dashboard & Analytics**
• Real-time job status tracking
• Performance metrics and reporting
• Team productivity insights`
      },
      {
        id: 'system-requirements',
        title: 'System Requirements',
        content: `**Browser Support**
• Chrome 90+ (Recommended)
• Firefox 88+
• Safari 14+
• Edge 90+

**Network Requirements**
• Stable internet connection
• HTTPS support required
• Minimum 1 Mbps upload/download speed`
      }
    ],
    tableOfContents: [
      { id: 'what-is-certs', title: 'What is Certs?' },
      { id: 'key-features', title: 'Key Features' },
      { id: 'system-requirements', title: 'System Requirements' }
    ]
  },
  'quick-start': {
    title: 'Quick Start Guide',
    description: 'Get up and running with Certs in just a few minutes. Follow this step-by-step guide to create your first job and certificate.',
    sections: [
      {
        id: 'first-login',
        title: '1. First Login & Dashboard',
        content: `After logging in, you'll see the main dashboard with:
• **Job Overview**: Current active jobs and their status
• **Quick Actions**: Create Job and New Certificate buttons
• **Recent Activity**: Latest updates and completions
• **Performance Metrics**: Key statistics and trends`
      },
      {
        id: 'create-first-job',
        title: '2. Creating Your First Job',
        content: `Click the "Create Job" button to start the 5-step process:

**Step 1: Job Details**
• Enter job title and description
• Select job type (Gas Safety, Electrical, etc.)
• Set priority level (Low, Medium, High)

**Step 2: Assign Engineer**
• Choose from available engineers
• Set scheduled date and time
• View engineer availability and workload

**Step 3: Customer Information**
• Select existing customer or create new
• Enter contact details and company information
• Manage customer database

**Step 4: Site Selection**
• Choose customer site or add new location
• Enter site address and access instructions
• Specify site type (Office, Warehouse, etc.)

**Step 5: Review & Confirm**
• Review all job details
• Confirm information is correct
• Submit job for processing`
      },
      {
        id: 'generate-certificate',
        title: '3. Generating Certificates',
        content: `Once a job is completed:
• Navigate to the Certificates page
• Click "New Certificate"
• Select the completed job
• Review certificate details
• Generate and download PDF certificate`
      }
    ],
    tableOfContents: [
      { id: 'first-login', title: 'First Login & Dashboard' },
      { id: 'create-first-job', title: 'Creating Your First Job' },
      { id: 'generate-certificate', title: 'Generating Certificates' }
    ]
  },
  'job-management': {
    title: 'Job Management',
    description: 'Learn how to effectively manage jobs throughout their lifecycle, from creation to completion.',
    sections: [
      {
        id: 'job-workflow',
        title: 'Job Workflow Overview',
        content: `Jobs in Certs follow a structured workflow:

**1. Created** - Job has been created and assigned
**2. Scheduled** - Engineer assigned with date/time
**3. In Progress** - Engineer is on-site working
**4. Completed** - Work finished, ready for certification
**5. Certified** - Certificate generated and delivered`
      },
      {
        id: 'job-types',
        title: 'Job Types & Categories',
        content: `Certs supports various certification types:

**Gas Safety Certificates**
• Annual gas safety inspections
• Appliance testing and certification
• Emergency gas work

**Electrical Certificates**
• PAT testing and certification
• Electrical installation certificates
• Emergency electrical work

**Fire Safety**
• Fire alarm testing
• Emergency lighting checks
• Fire risk assessments`
      },
      {
        id: 'scheduling',
        title: 'Scheduling & Assignment',
        content: `Efficient scheduling features:
• **Engineer Availability**: View real-time availability
• **Workload Management**: Balance assignments across team
• **Priority Scheduling**: High-priority jobs get preference
• **Automatic Notifications**: Engineers receive job assignments
• **Calendar Integration**: Sync with external calendars`
      }
    ],
    tableOfContents: [
      { id: 'job-workflow', title: 'Job Workflow Overview' },
      { id: 'job-types', title: 'Job Types & Categories' },
      { id: 'scheduling', title: 'Scheduling & Assignment' }
    ]
  },
  'customer-management': {
    title: 'Customer & Site Management',
    description: 'Manage your customer database and site locations effectively for streamlined job creation.',
    sections: [
      {
        id: 'customer-database',
        title: 'Customer Database',
        content: `Build and maintain your customer relationships:

**Customer Profiles Include:**
• Company name and contact person
• Phone, email, and address details
• Service history and preferences
• Multiple site locations
• Billing and invoicing information

**Customer Management Features:**
• Quick search and filtering
• Import/export customer data
• Duplicate detection and merging
• Customer communication history`
      },
      {
        id: 'site-management',
        title: 'Site Management',
        content: `Each customer can have multiple sites:

**Site Information:**
• Site name and type (Office, Warehouse, Retail)
• Full address and postcode
• Access instructions and key holder details
• Site-specific requirements and notes
• Historical job records per site

**Site Types Supported:**
• Office buildings
• Warehouses and distribution centers
• Retail locations
• Residential properties
• Industrial facilities`
      }
    ],
    tableOfContents: [
      { id: 'customer-database', title: 'Customer Database' },
      { id: 'site-management', title: 'Site Management' }
    ]
  }
};

const helpGuidesData: { [key: string]: any } = {
  'video-tutorials': {
    title: 'Video Tutorials',
    description: 'Watch step-by-step video guides to master Certs platform features.',
    sections: [
      {
        id: 'getting-started-video',
        title: 'Getting Started (5 min)',
        content: `Learn the basics of navigating Certs and creating your first job.`,
        videoId: 'getting-started-demo',
        thumbnail: '/video-thumbnails/getting-started.jpg'
      },
      {
        id: 'job-creation-video',
        title: 'Creating Jobs (8 min)',
        content: `Detailed walkthrough of the 5-step job creation process.`,
        videoId: 'job-creation-demo',
        thumbnail: '/video-thumbnails/job-creation.jpg'
      },
      {
        id: 'certificate-generation-video',
        title: 'Certificate Generation (6 min)',
        content: `How to generate, customize, and deliver certificates to customers.`,
        videoId: 'certificate-demo',
        thumbnail: '/video-thumbnails/certificates.jpg'
      }
    ],
    tableOfContents: [
      { id: 'getting-started-video', title: 'Getting Started' },
      { id: 'job-creation-video', title: 'Creating Jobs' },
      { id: 'certificate-generation-video', title: 'Certificate Generation' }
    ]
  }
};

export default function Documentation() {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState<string[]>(['getting-started']);
  const [activeContent, setActiveContent] = useState('getting-started-overview');
  const [activeTab, setActiveTab] = useState<'documentation' | 'help-guides'>('documentation');

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleNavClick = (contentId: string) => {
    setActiveContent(contentId);
  };

  const renderNavItem = (item: NavItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = activeContent === item.content;

    return (
      <div key={item.id} className={`nav-item level-${level}`}>
        <div
          className={`nav-item-content ${isActive ? 'active' : ''}`}
          onClick={() => {
            if (hasChildren) {
              toggleExpanded(item.id);
            } else if (item.content) {
              handleNavClick(item.content);
            }
          }}
        >
          <div className="nav-item-left">
            {item.icon && <item.icon className="nav-icon" />}
            <span className="nav-title">{item.title}</span>
          </div>
          {hasChildren && (
            <div className="nav-chevron">
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </div>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="nav-children">
            {item.children?.map(child => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const currentContent = activeTab === 'documentation' 
    ? contentData[activeContent] 
    : helpGuidesData[activeContent];

  return (
    <div className="docs-container">
      {/* Header */}
      <div className="docs-header">
        <div className="docs-header-left">
          <div className="docs-logo">
            <img src="/icon.png" alt="Certs" className="logo-image" />
          </div>
          <div className="search-container">
            <MagnifyingGlassIcon className="search-icon" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        
        <div className="docs-header-right">
          <button className="header-btn">Feedback</button>
          <button className="header-btn">Changelog</button>
          <button className="header-btn">Help Center</button>
          <div className="user-avatar">
            <div className="avatar-circle">A</div>
          </div>
        </div>
      </div>

      {/* Tab Section */}
      <div className="docs-tabs">
        <div className="tab-container">
          <button
            className={`tab-button ${activeTab === 'documentation' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('documentation');
              setActiveContent('getting-started-overview');
              setExpandedItems(['getting-started']);
            }}
          >
            Documentation
          </button>
          <button
            className={`tab-button ${activeTab === 'help-guides' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('help-guides');
              setActiveContent('video-tutorials');
              setExpandedItems(['video-tutorials']);
            }}
          >
            Help Guides
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="docs-main">
        {/* Sidebar Navigation */}
        <div className="docs-sidebar">
          <nav className="docs-nav">
            {activeTab === 'documentation' 
              ? navigationData.map(item => renderNavItem(item))
              : helpGuidesNavigation.map(item => renderNavItem(item))
            }
          </nav>
        </div>

        {/* Content Area */}
        <div className="docs-content">
          <div className="content-wrapper">
            {currentContent && (
              <>
                <div className="content-header">
                  <div className="breadcrumb">
                    <span>Documentation</span>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                    <span>Compliance Measures</span>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900">{currentContent.title}</span>
                  </div>
                  <h1 className="content-title">{currentContent.title}</h1>
                  <p className="content-description">{currentContent.description}</p>
                </div>

                <div className="content-body">
                  {currentContent.sections?.map((section: any) => (
                    <div key={section.id} className="content-section" id={section.id}>
                      <h2 className="section-title">{section.title}</h2>
                      <p className="section-content">{section.content}</p>
                      
                      {section.videoId && (
                        <div className="video-container">
                          <div className="video-placeholder">
                            <div className="video-thumbnail">
                              <div className="play-button">
                                <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="video-info">
                                <h4>Watch: {section.title}</h4>
                                <p>Click to play video tutorial</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {section.code && (
                        <div className="code-block">
                          <div className="code-header">
                            <div className="code-language">TypeScript</div>
                            <button className="copy-code-btn">Copy code</button>
                          </div>
                          <pre className="code-content">
                            <code>{section.code}</code>
                          </pre>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Table of Contents */}
        <div className="docs-toc">
          <div className="toc-header">
            <h3>On this page</h3>
          </div>
          <div className="toc-content">
            {currentContent?.tableOfContents?.map((item: any) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="toc-item"
              >
                {item.title}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
